import {Bracket} from "../../../../data/models/bracket_api/Bracket";
import {Result} from "../../../../data/models/bracket_api/Result";
import {Pick} from "../../../../data/models/bracket_api/Pick";
import {Driver} from "../../../../data/models/bracket_api/Driver";
import {Event} from "../../../../data/models/bracket_api/Event";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import {useTheme} from "@mui/material";
import PickBlockTopMoments from "./PickBlockTopMoments";
import BracketConnector from "../../BracketConnector";
import Typography from "@mui/material/Typography";
import BracketConnectorReverse from "../../BracketConnectorReverse";
import PickBlockReverseTopMoments from "./PickBlockReverseTopMoments";

export type PickBracketTopMomentsProps = {
    quadrant?: number, // 0=TopLeft, 1=TopRight, 2=BottomLeft, 3=BottomRight, undefined=center
    bracket?: Bracket,
    results?: Result[],
    picks?: Pick[],
    updatePick: (pick: Pick) => void,
    picks_locked: boolean,
    event?: Event,
}

export default function PickBracketTopMoments(props: Readonly<PickBracketTopMomentsProps>) {
    let winner: Driver | undefined;
    const theme = useTheme();
    const connectorColor = theme.palette.mode === 'dark' ? '#ffffff' : '#000000';
    
    // If quadrant is undefined, this is the center section (rounds 5-6)
    if (props.quadrant === undefined) {
        // Check picks first, then results
        winner = props.picks?.find(pick => {
            return pick.result.round === 6 && pick.winner
        })?.winner;
        
        if (!winner) {
            winner = props.results?.find(result => {
                return result.round === 6 && result.winner
            })?.winner;
        }
        
        return (
            <Grid container sx={{
                display: 'flex',
                flexDirection: "row",
                position: "relative",
                minWidth: 'fit-content',
                flexWrap: "nowrap",
            }}>
                {/* Round 5 - Final Four Game 1 (left side) */}
                <Grid container sx={{
                    flex: .2,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    alignContent: "center"
                }}>
                    <PickBlockReverseTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 5 && pick.result.game === 1
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 5 && result.game === 1
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                </Grid>
                <Grid container sx={{
                    flex: .1,
                    display: 'flex',
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center"
                }}>
                    {/* Straight horizontal line connector */}
                    <hr style={{
                        width: '100%',
                        border: 'none',
                        borderTop: `2px solid ${connectorColor}`,
                        margin: 0
                    }} />
                </Grid>
                {/* Round 6 - Championship (center) */}
                <Grid container sx={{
                    flex: .2,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "center",
                    position: "relative",
                }}>
                    {props.event?.left_sponsor_image_link && (
                        <Box sx={{
                            position: "absolute",
                            top: "-200px",
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}>
                            <img 
                                src={props.event.left_sponsor_image_link} 
                                alt={"Sponsor Logo"} 
                                style={{maxHeight: '250px', maxWidth: '250px'}}
                            />
                        </Box>
                    )}
                    {winner && (
                        <Typography variant="h6" component="div" textAlign={"center"} position={"absolute"} top={"-80px"} width={"100%"}>
                            Winner: {winner.first_name + " " + winner.last_name}
                        </Typography>
                    )}
                    <PickBlockTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 6 && pick.result.game === 1
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 6 && result.game === 1
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                    {props.event?.right_sponsor_image_link && (
                        <Box sx={{
                            position: "absolute",
                            bottom: "-200px",
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}>
                            <img 
                                src={props.event.right_sponsor_image_link} 
                                alt={"Sponsor Logo"} 
                                style={{maxHeight: '250px', maxWidth: '250px'}}
                            />
                        </Box>
                    )}
                </Grid>
                <Grid container sx={{
                    flex: .1,
                    display: 'flex',
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center"
                }}>
                    {/* Straight horizontal line connector */}
                    <hr style={{
                        width: '100%',
                        border: 'none',
                        borderTop: `2px solid ${connectorColor}`,
                        margin: 0
                    }} />
                </Grid>
                {/* Round 5 - Final Four Game 2 (right side) */}
                <Grid container sx={{
                    flex: .2,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    alignContent: "center"
                }}>
                    <PickBlockReverseTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 5 && pick.result.game === 2
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 5 && result.game === 2
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                </Grid>
            </Grid>
        );
    }
    
    // Render a quadrant (16-entry bracket, rounds 1-4)
    // Calculate game numbers for this quadrant
    const round1StartGame = props.quadrant * 8 + 1;
    const round2StartGame = props.quadrant * 4 + 1;
    const round3StartGame = props.quadrant * 2 + 1;
    const round4Game = props.quadrant + 1;
    
    // Use reverse layout for quadrants 1 and 3 (right side)
    const useReverse = props.quadrant === 1 || props.quadrant === 3;
    
    if (useReverse) {
        return (
            <Grid container sx={{
                display: 'flex',
                flexDirection: "row",
                position: "relative",
                minWidth: 'fit-content',
                flexWrap: "nowrap",
            }}>
                <Grid container sx={{
                    flex: .2,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "center",
                    position: "relative",
                }}>
                    {/* Round 4 */}
                    <PickBlockReverseTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 4 && pick.result.game === round4Game
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 4 && result.game === round4Game
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                </Grid>
                <Grid container sx={{
                    flex: .1,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "space-evenly",
                    alignItems: "center"
                }}>
                    <BracketConnectorReverse height={"51%"}/>
                </Grid>
                <Grid container sx={{
                    flex: .2,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    alignContent: "center"
                }}>
                    {/* Round 3 */}
                    <PickBlockReverseTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 3 && pick.result.game === round3StartGame
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 3 && result.game === round3StartGame
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                    {/* Spacer to replace emblem for proper spacing */}
                    <Box sx={{width: '270px', height: '270px'}} />
                    <PickBlockReverseTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 3 && pick.result.game === round3StartGame + 1
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 3 && result.game === round3StartGame + 1
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                </Grid>
                <Grid container sx={{
                    flex: .1,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "space-around",
                    alignItems: "center"
                }}>
                    <BracketConnectorReverse height={"25.5%"}/>
                    <BracketConnectorReverse height={"25.5%"}/>
                </Grid>
                <Grid container sx={{
                    flex: .2,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "space-around",
                    alignItems: "center",
                    height: '100%',
                }}>
                    {/* Round 2 */}
                    <PickBlockReverseTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 2 && pick.result.game === round2StartGame
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 2 && result.game === round2StartGame
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                    <PickBlockReverseTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 2 && pick.result.game === round2StartGame + 1
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 2 && result.game === round2StartGame + 1
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                    <PickBlockReverseTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 2 && pick.result.game === round2StartGame + 2
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 2 && result.game === round2StartGame + 2
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                    <PickBlockReverseTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 2 && pick.result.game === round2StartGame + 3
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 2 && result.game === round2StartGame + 3
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                </Grid>
                <Grid container sx={{
                    flex: .1,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "space-around",
                    alignItems: "center",
                    height: '100%',
                }}>
                    <BracketConnectorReverse height={"12.75%"}/>
                    <BracketConnectorReverse height={"12.75%"}/>
                    <BracketConnectorReverse height={"12.75%"}/>
                    <BracketConnectorReverse height={"12.75%"}/>
                </Grid>
                <Grid container sx={{
                    flex: .2,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "space-evenly",
                    alignItems: "center",
                    height: '100%',
                }}>
                    {/* Round 1 */}
                    <PickBlockReverseTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 1 && pick.result.game === round1StartGame
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                    <PickBlockReverseTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 1 && pick.result.game === round1StartGame + 1
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 1
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                    <PickBlockReverseTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 1 && pick.result.game === round1StartGame + 2
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 2
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                    <PickBlockReverseTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 1 && pick.result.game === round1StartGame + 3
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 3
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                    <PickBlockReverseTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 1 && pick.result.game === round1StartGame + 4
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 4
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                    <PickBlockReverseTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 1 && pick.result.game === round1StartGame + 5
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 5
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                    <PickBlockReverseTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 1 && pick.result.game === round1StartGame + 6
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 6
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                    <PickBlockReverseTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 1 && pick.result.game === round1StartGame + 7
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 7
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                </Grid>
            </Grid>
        );
    } else {
        // Forward layout for quadrants 0 and 2 (left side)
        return (
            <Grid container sx={{
                display: 'flex',
                flexDirection: "row",
                position: "relative",
                minWidth: 'fit-content',
                flexWrap: "nowrap",
            }}>
                <Grid container sx={{
                    flex: .2,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "space-evenly",
                    alignItems: "center",
                    height: '100%',
                }}>
                    {/* Round 1 */}
                    <PickBlockTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 1 && pick.result.game === round1StartGame
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                    <PickBlockTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 1 && pick.result.game === round1StartGame + 1
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 1
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                    <PickBlockTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 1 && pick.result.game === round1StartGame + 2
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 2
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                    <PickBlockTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 1 && pick.result.game === round1StartGame + 3
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 3
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                    <PickBlockTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 1 && pick.result.game === round1StartGame + 4
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 4
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                    <PickBlockTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 1 && pick.result.game === round1StartGame + 5
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 5
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                    <PickBlockTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 1 && pick.result.game === round1StartGame + 6
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 6
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                    <PickBlockTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 1 && pick.result.game === round1StartGame + 7
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 7
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                </Grid>
                <Grid container sx={{
                    flex: .1,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "space-around",
                    alignItems: "center",
                    height: '100%',
                }}>
                    <BracketConnector height={"12.75%"}/>
                    <BracketConnector height={"12.75%"}/>
                    <BracketConnector height={"12.75%"}/>
                    <BracketConnector height={"12.75%"}/>
                </Grid>
                <Grid container sx={{
                    flex: .2,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "space-around",
                    alignItems: "center",
                    height: '100%',
                }}>
                    {/* Round 2 */}
                    <PickBlockTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 2 && pick.result.game === round2StartGame
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 2 && result.game === round2StartGame
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                    <PickBlockTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 2 && pick.result.game === round2StartGame + 1
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 2 && result.game === round2StartGame + 1
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                    <PickBlockTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 2 && pick.result.game === round2StartGame + 2
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 2 && result.game === round2StartGame + 2
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                    <PickBlockTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 2 && pick.result.game === round2StartGame + 3
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 2 && result.game === round2StartGame + 3
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                </Grid>
                <Grid container sx={{
                    flex: .1,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "space-around",
                    alignItems: "center"
                }}>
                    <BracketConnector height={"25.5%"}/>
                    <BracketConnector height={"25.5%"}/>
                </Grid>
                <Grid container sx={{
                    flex: .2,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    alignContent: "center"
                }}>
                    {/* Round 3 */}
                    <PickBlockTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 3 && pick.result.game === round3StartGame
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 3 && result.game === round3StartGame
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                    {/* Spacer to replace emblem for proper spacing */}
                    <Box sx={{width: '270px', height: '270px'}} />
                    <PickBlockTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 3 && pick.result.game === round3StartGame + 1
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 3 && result.game === round3StartGame + 1
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                </Grid>
                <Grid container sx={{
                    flex: .1,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "space-evenly",
                    alignItems: "center"
                }}>
                    <BracketConnector height={"51%"}/>
                </Grid>
                <Grid container sx={{
                    flex: .2,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "center",
                    position: "relative",
                }}>
                    {/* Round 4 */}
                    <PickBlockTopMoments
                        pick={props.picks?.find((pick) => {
                            return pick.result.round === 4 && pick.result.game === round4Game
                        })}
                        result={props.results?.find((result) => {
                            return result.round === 4 && result.game === round4Game
                        })}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                </Grid>
            </Grid>
        );
    }
}
